import React, { useState, useRef, useMemo } from "react";
import AssociationList from "../AssociationList";
import { CONSTANTS, getTranslation } from "common";
import { useTranslations } from "hooks";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { Resizable } from "re-resizable";
import SearchBar from "components/SearchBar";
//@ts-ignore
import {
  GroupModel,
  RolesModel,
  UsersOfSelectedOrganization,
} from "../NewProcessModule";
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { ExtensionElements } from "model/processModel";

const OrganizationPanel = ({
  extensionElements,
  handleCanvasViewPort,
  orgPanelPositionRef,
  allRolesOfSelectedOrganization,
  allGroupsOfSelectedOrganization,
  allUsersOfSelectedOrganization,
  getUsername,
  onLaneMouseDown,
  selectedLane,
  isOverlapChecked,
  setIsOverlapChecked,
}: {
  handleCanvasViewPort: () => void;
  extensionElements: ExtensionElements | undefined;
  orgPanelPositionRef: any;
  allRolesOfSelectedOrganization: Array<RolesModel>;
  allGroupsOfSelectedOrganization: Array<GroupModel>;
  allUsersOfSelectedOrganization: Array<UsersOfSelectedOrganization>;
  getUsername: (value: UsersOfSelectedOrganization) => string;
  onLaneMouseDown: (event: any, data: any) => void;
  selectedLane: string;
  isOverlapChecked: boolean;
  setIsOverlapChecked: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [filterText, setFilterText] = useState("");

  const [showOppAccordian, setShowOppAccordian] = useState<boolean>(false);

  const resizableElementRef = useRef<any>(null);
  const oppAccordianRef = useRef<any>(null);
  const translations = useTranslations();

  const listOfRolesToBeDisplayed: {
    list: Array<RolesModel>;
  } = useMemo(() => {
    if (filterText) {
      const list = allRolesOfSelectedOrganization.filter((org: RolesModel) =>
        org.name.toLocaleLowerCase().includes(filterText)
      );
      return {
        list,
      };
    } else {
      return {
        list: allRolesOfSelectedOrganization,
      };
    }
  }, [allRolesOfSelectedOrganization, filterText]);

  const listOfGroupsToBeDisplayed: {
    list: Array<GroupModel>;
  } = useMemo(() => {
    if (filterText) {
      const list = allGroupsOfSelectedOrganization.filter((org: GroupModel) =>
        org.name.toLocaleLowerCase().includes(filterText)
      );
      return {
        list,
      };
    } else {
      return {
        list: allGroupsOfSelectedOrganization,
      };
    }
  }, [allGroupsOfSelectedOrganization, filterText]);

  const listOfUsersToBeDisplayed: {
    list: Array<UsersOfSelectedOrganization>;
  } = useMemo(() => {
    if (filterText) {
      const list = allUsersOfSelectedOrganization
        .filter((org: UsersOfSelectedOrganization) => {
          return (
            (org.username || org.lastname) &&
            (`${org.username} ${org.lastname}`
              .trim()
              .toLocaleLowerCase()
              .includes(filterText.toLocaleLowerCase()) ||
              `${org.email}`
                .trim()
                .toLocaleLowerCase()
                .includes(filterText.toLocaleLowerCase()))
          );
        })
        .sort();
      return {
        list,
      };
    } else {
      return {
        list: allUsersOfSelectedOrganization,
      };
    }
  }, [allUsersOfSelectedOrganization, filterText]);

  const handleChangeOverlap = (
    e: React.MouseEvent<HTMLLabelElement, MouseEvent>
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOverlapChecked((_: boolean) => {
      localStorage.setItem(CONSTANTS.STORE_OVERLAP_ENABLED, JSON.stringify(!_));
      return !_;
    });
  };

  return (
    <Resizable
      ref={resizableElementRef}
      className={`resizable-container ${
        isOverlapChecked
          ? "resizable-container-absolute"
          : "resizable-container-relative"
      }`}
      onResizeStart={() => {
        // setShouldResizeAutomatically(false);
        handleCanvasViewPort();
      }}
      onResizeStop={(...rest) => {
        if (Number(rest[2].style.height.replace("px", "")) <= 64) {
          orgPanelPositionRef.current = "64px";
          resizableElementRef.current.resizable.style.height = "64px";
          setShowOppAccordian(false);
        } else {
          // setDefaultOrgPos(resizableElementRef.current.resizable.style.height);
          orgPanelPositionRef.current = rest[2].style.height;
        }
        // setShouldResizeAutomatically(false);
        handleCanvasViewPort();
      }}
      onResize={() => {
        handleCanvasViewPort();
      }}
      enable={{
        top: showOppAccordian,
        right: false,
        bottom: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
    >
      <Accordion
        disableGutters
        id="organization-action-container"
        // component={Paper}
        TransitionComponent={Collapse}
        TransitionProps={{ style: { flex: 1, overflowY: "auto" } }}
        sx={{
          overflowY: showOppAccordian ? "auto" : "hidden",
          width: (_) => `calc(100% + ${_.spacing(1)})`,
          ml: (_) => `-${_.spacing(1)}`,
          clipPath: "inset(-200px 0px 0px 0px)",
          borderRadius: 0,
          height: "100%",
        }}
        expanded={showOppAccordian}
        onChange={(_, expanded) =>
          expanded
            ? (resizableElementRef.current.resizable.style.height = "200px")
            : (resizableElementRef.current.resizable.style.height = "64px")
        }
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          onClick={(e) => {
            e.stopPropagation();
            if (showOppAccordian) {
              const reducableHeight = oppAccordianRef.current.clientHeight;
              const originalHeight =
                resizableElementRef.current.resizable.clientHeight;
              const resultantHeight = originalHeight - reducableHeight;
              resizableElementRef.current.resizable.style.height = `${resultantHeight}px`;
            }
            setShowOppAccordian(!showOppAccordian);
          }}
        >
          <Stack
            flex={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack alignItems="center" direction="row" spacing={2}>
                <Typography variant="h6">
                  {getTranslation(translations, "pm.organizationchart.lable")}
                </Typography>
                <Box
                  onClick={(e) => {
                    if (showOppAccordian) {
                      e.stopPropagation();
                    }
                  }}
                >
                  <SearchBar
                    id="search-bar-panel-org"
                    placeholder={getTranslation(
                      translations,
                      "pm.organizationchart.ricerca.placeholder"
                    )}
                    setFilter={(text: string) => {
                      setFilterText(text.toLowerCase());
                    }}
                  />
                </Box>
              </Stack>
              {/* <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilter();
                }}
              >
                <Sort />
              </IconButton> */}
            </Stack>
            <FormControlLabel
              onClick={handleChangeOverlap}
              control={<Checkbox checked={isOverlapChecked} />}
              label={getTranslation(
                translations,
                "pm.organizationchart.overlapped.label"
              )}
            />
          </Stack>
        </AccordionSummary>
        <AssociationList
          extensionElements={extensionElements}
          oppAccordianRef={oppAccordianRef}
          listOfUsersToBeDisplayed={listOfUsersToBeDisplayed.list}
          getUsername={getUsername}
          onLaneMouseDown={onLaneMouseDown}
          selectedLane={selectedLane}
          listOfGroupsToBeDisplayed={listOfGroupsToBeDisplayed.list}
          listOfRolesToBeDisplayed={listOfRolesToBeDisplayed.list}
        />
      </Accordion>
    </Resizable>
  );
};

export default OrganizationPanel;
